import { BlogPost, CaseStudy, Service, TestimonialData } from '../types/types';

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresIn: number;
}

class Cache {
  private static instance: Cache;
  private storage: Map<string, CacheItem<unknown>>;
  private readonly DEFAULT_EXPIRY = 5 * 60 * 1000; // 5 minutes in milliseconds

  private constructor() {
    this.storage = new Map();
  }

  public static getInstance(): Cache {
    if (!Cache.instance) {
      Cache.instance = new Cache();
    }
    return Cache.instance;
  }

  public set<T>(key: string, data: T, expiresIn: number = this.DEFAULT_EXPIRY): void {
    this.storage.set(key, {
      data,
      timestamp: Date.now(),
      expiresIn,
    });
  }

  public get<T>(key: string): T | null {
    const item = this.storage.get(key);
    if (!item) return null;

    const now = Date.now();
    if (now - item.timestamp > item.expiresIn) {
      this.storage.delete(key);
      return null;
    }

    return item.data as T;
  }

  public clear(): void {
    this.storage.clear();
  }

  public remove(key: string): void {
    this.storage.delete(key);
  }

  public has(key: string): boolean {
    return this.storage.has(key);
  }
}

// Cache keys
export const CACHE_KEYS = {
  SERVICES: 'services',
  BLOG_POSTS: 'blog-posts',
  CASE_STUDIES: 'case-studies',
  TESTIMONIALS: 'testimonials',
} as const;

// Export singleton instance
export const cache = Cache.getInstance();

// Type-safe cache helpers
export const cacheServices = (services: Service[]) => cache.set(CACHE_KEYS.SERVICES, services);
export const getCachedServices = () => cache.get<Service[]>(CACHE_KEYS.SERVICES);

export const cacheBlogPosts = (posts: BlogPost[]) => cache.set(CACHE_KEYS.BLOG_POSTS, posts);
export const getCachedBlogPosts = () => cache.get<BlogPost[]>(CACHE_KEYS.BLOG_POSTS);

export const cacheCaseStudies = (studies: CaseStudy[]) =>
  cache.set(CACHE_KEYS.CASE_STUDIES, studies);
export const getCachedCaseStudies = () => cache.get<CaseStudy[]>(CACHE_KEYS.CASE_STUDIES);

export const cacheTestimonials = (testimonials: TestimonialData[]) =>
  cache.set(CACHE_KEYS.TESTIMONIALS, testimonials);
export const getCachedTestimonials = () => cache.get<TestimonialData[]>(CACHE_KEYS.TESTIMONIALS);
